/**
 * StoryPageTabs — the routed tab strip for the story page (ADR-0081 §2;
 * a11y contract #1). A `<nav aria-label>` of real router links — NOT a
 * `role=tablist`/`role=tab` widget (that fights the router and breaks
 * deep-linking). The active tab carries `aria-current="page"`; the active
 * underline keys off `aria-current` so exactly one tab is highlighted and
 * it is URL-driven.
 *
 * packages/ui must not import the app router, so the consumer supplies a
 * `renderLink` prop that wraps each item in its router `<Link>`. The
 * consumer is responsible for setting `aria-current="page"` on the active
 * link (TanStack Router does this via `activeProps`); this component styles
 * the active underline purely off the `[aria-current=page]` selector, and
 * also accepts an `active` flag per item as a fallback for routers that
 * don't emit aria-current automatically.
 *
 * Motion (the underline) is gated behind motion-safe:.
 */

import {
  type AnchorHTMLAttributes,
  type ReactElement,
  type ReactNode,
} from "react";

import { cn } from "../lib/utils";

export interface StoryPageTabItem {
  key: string;
  label: string;
  /** Target route (consumer's router builds the actual href/Link). */
  to: string;
  /** Optional count badge (e.g. Related (3)). */
  count?: number;
  /** Stable testid for the tab link. */
  testId?: string;
  /**
   * Stable testid for the count badge itself. Needed where a journey checks
   * that the badge agrees with what rendered — "3 lines means 3 lines, or the
   * badge disagrees with the accounting file" (SMV-E, criterion E.20).
   */
  countTestId?: string;
  /** Fallback active flag for routers that don't emit aria-current. */
  active?: boolean;
}

export interface StoryPageTabsProps {
  items: StoryPageTabItem[];
  /**
   * Wraps the tab's inner content in the host router's <Link to={item.to}>.
   * The host should pass activeProps that set aria-current="page" on the
   * active link. `linkProps` carries the shared className + testid this
   * component computes; spread them onto the Link.
   */
  renderLink: (
    item: StoryPageTabItem,
    children: ReactNode,
    linkProps: AnchorHTMLAttributes<HTMLAnchorElement> & {
      "data-testid"?: string;
    },
  ) => ReactNode;
  /** Accessible name for the <nav>. */
  ariaLabel?: string;
  /**
   * Per-module testid namespace, e.g. "invoice-story" / "customer-story".
   * The nav emits `${idPrefix}-tabs`; each tab defaults to
   * `${idPrefix}-tab-${item.key}` (an item's own `testId` still wins).
   * Defaults to "invoice-story" so the invoicing call site is unchanged.
   */
  idPrefix?: string;
  className?: string;
}

/** Shared classes the consumer can apply directly if not using renderLink. */
export const storyPageTabLinkClass = cn(
  "relative inline-flex items-center gap-1.5 whitespace-nowrap px-4 py-3",
  "text-sm font-medium text-[var(--color-text-soft)]",
  "transition-colors hover:text-[var(--color-text-strong)]",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]",
  // Active underline — keyed off aria-current OR the data-active fallback.
  "aria-[current=page]:text-[var(--color-brand-strong)]",
  "data-[active=true]:text-[var(--color-brand-strong)]",
  "after:absolute after:inset-x-3 after:bottom-0 after:h-0.5 after:rounded-full",
  "after:bg-[var(--color-brand)] after:opacity-0 after:scale-x-0",
  "motion-safe:after:transition-[opacity,transform] motion-safe:after:duration-200",
  "aria-[current=page]:after:opacity-100 aria-[current=page]:after:scale-x-100",
  "data-[active=true]:after:opacity-100 data-[active=true]:after:scale-x-100",
);

export function StoryPageTabs({
  items,
  renderLink,
  ariaLabel = "Record sections",
  idPrefix = "invoice-story",
  className,
}: StoryPageTabsProps): ReactElement {
  return (
    <nav
      aria-label={ariaLabel}
      data-testid={`${idPrefix}-tabs`}
      className={cn(
        "flex gap-0 overflow-x-auto border-b border-[var(--color-border-hairline)]",
        "bg-[var(--color-surface)] px-6 lg:px-8",
        className,
      )}
    >
      {items.map((item) => {
        const inner = (
          <>
            <span>{item.label}</span>
            {typeof item.count === "number" ? (
              <span
                data-testid={item.countTestId}
                className="inline-grid h-5 min-w-[20px] place-items-center rounded-full bg-[var(--color-surface-soft)] px-1.5 text-[11px] tabular-nums text-[var(--color-text-soft)]"
              >
                {item.count}
              </span>
            ) : null}
          </>
        );
        const linkProps: AnchorHTMLAttributes<HTMLAnchorElement> & {
          "data-testid"?: string;
          "data-active"?: string;
        } = {
          className: storyPageTabLinkClass,
          "data-testid": item.testId ?? `${idPrefix}-tab-${item.key}`,
        };
        if (item.active) {
          linkProps["data-active"] = "true";
          linkProps["aria-current"] = "page";
        }
        return (
          <span key={item.key}>{renderLink(item, inner, linkProps)}</span>
        );
      })}
    </nav>
  );
}
